/**
 * iter86.77.8 — Live score ticker for football tournament pages.
 *
 * Horizontal strip of today's games. Zero vendor cost — reads the
 * poller-maintained schedule docs, which refresh every ~2 min during
 * live windows. Polls every 30s while a game is live, 60s otherwise,
 * and renders NOTHING outside game windows.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { nflTeamColorPair } from "@/lib/nflTeamColors";
import { readableTextColor } from "@/components/dashboard/TeamSwatch";

const kickoffLabel = (iso) => {
    try {
        return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    } catch {
        return "";
    }
};

const statusBadge = (g) => {
    if (g.in_progress) return { label: g.live_detail || g.status || "LIVE", live: true };
    if (g.played) return { label: "FINAL", live: false };
    return { label: kickoffLabel(g.kickoff), live: false };
};

// iter87.212.19 — No vendor team logos anywhere in the app (licensing
// risk — API-SPORTS explicitly disclaims IP rights on the crest images
// it serves, and league/team trademarks aren't ours to display). A
// solid single-color initials swatch (real NFL brand primary color
// from `lib/nflTeamColors.js`; iter87.260 — reverted from two-tone
// diagonal-split to a single color per operator directive) replaces
// the old `<img>` — same at-a-glance team recognition, zero
// third-party imagery.
function initials(name) {
    const words = (name || "").trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return "?";
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return words.map((w) => w[0]).join("").slice(0, 3).toUpperCase();
}

function TeamRow({ name, score, winner, mine }) {
    const [primary] = nflTeamColorPair(name);
    // iter87.511 (user-reported — same white-on-light-color fix as
    // TeamSwatch.jsx) — halo flips direction with the text color so
    // it still reads as an outline, not a smudge, either way.
    const textColor = readableTextColor(primary);
    const haloColor = textColor === "#000000" ? "#fff" : "#000";
    return (
        <div className="flex items-center gap-1.5 min-w-0">
            <span
                className="w-4 h-4 shrink-0 rounded-full flex items-center justify-center text-[7px] font-black tracking-tighter leading-none"
                style={{
                    background: primary,
                    color: textColor,
                    textShadow: `-1px -1px 0 ${haloColor}, 1px -1px 0 ${haloColor}, -1px 1px 0 ${haloColor}, 1px 1px 0 ${haloColor}`,
                }}
                data-testid="nfl-team-swatch"
            >
                {initials(name)}
            </span>
            <span className={`text-[11px] truncate ${mine ? "text-volt font-bold" : winner ? "text-white font-bold" : "text-zinc-300"}`}>{name}</span>
            <span className={`ml-auto text-[11px] font-mono ${winner ? "text-white font-bold" : "text-zinc-400"}`}>
                {score ?? "–"}
            </span>
        </div>
    );
}

export default function NflLiveTicker({ tournamentId, myTeamIds = [], sport = "nfl", onOpenSchedule }) {
    const [data, setData] = useState(null);
    const timer = useRef(null);
    // iter87.111 — API + test-id resolve from the `sport` prop so one
    // component serves NFL Season and NFL Weekly Spread Boost
    // (`sport="nfl_boost"`).
    const fetchTicker = sport === "nfl_boost" ? api.nflBoostLiveTicker : api.nflLiveTicker;
    const testIdBase = sport === "nfl_boost" ? "nfl-boost-live-ticker" : "nfl-live-ticker";

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const d = await fetchTicker(tournamentId);
                if (cancelled) return;
                setData(d);
                clearTimeout(timer.current);
                timer.current = setTimeout(load, d.live_window ? 30000 : 60000);
            } catch {
                if (!cancelled) timer.current = setTimeout(load, 120000);
            }
        };
        load();
        return () => { cancelled = true; clearTimeout(timer.current); };
    }, [tournamentId, fetchTicker]);

    const games = data?.games || [];
    if (!games.length) return null;

    return (
        <div
            className="border border-[#222] bg-[#0a0a0a] px-3 py-2 rounded-lg"
            data-testid={testIdBase}
        >
            <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                    {data.live_window ? "Games On Now" : "Today's Games"}
                </span>
                {data.live_window && (
                    <span className="flex items-center gap-1 text-[10px] font-bold uppercase text-neon" data-testid={`${testIdBase}-live-pill`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-neon animate-pulse" /> Live
                    </span>
                )}
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1" data-testid={`${testIdBase}-games`}>
                {games.map((g) => {
                    const b = statusBadge(g);
                    const homeWin = g.played && g.home_score != null && g.away_score != null && Number(g.home_score) > Number(g.away_score);
                    const awayWin = g.played && g.home_score != null && g.away_score != null && Number(g.away_score) > Number(g.home_score);
                    // iter87.53 — For CFB tournaments there are no
                    // assigned teams; the caller passes `myTeamIds`
                    // derived from the player's current-week boost
                    // picks so the "MY MATCHUP" highlight still fires.
                    const homeMine = myTeamIds.includes(g.home_team_id);
                    const awayMine = myTeamIds.includes(g.away_team_id);
                    const mine = homeMine || awayMine;
                    const border = g.in_progress ? "border-neon/50 bg-neon/5" : "border-[#222] bg-[#111]";
                    return (
                        <div
                            key={g.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => onOpenSchedule?.(g)}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onOpenSchedule?.(g); }}
                            className={`shrink-0 w-44 p-2 rounded-lg border ${border} ${mine ? "border-l-2 border-l-volt bg-volt/5" : ""} ${onOpenSchedule ? "cursor-pointer hover:brightness-125 transition-[filter]" : ""}`}
                            data-testid={`${testIdBase}-game-${g.id}`}
                            data-my-matchup={mine ? "true" : undefined}
                        >
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-[9px] uppercase tracking-wider text-zinc-500">
                                    {mine ? <span className="text-volt font-bold">MY MATCHUP</span> : (g.week || "")}
                                </span>
                                <span className={`text-[9px] font-bold uppercase tracking-wider ${b.live ? "text-neon" : g.played ? "text-zinc-400" : "text-cyber"}`}>
                                    {b.label}
                                </span>
                            </div>
                            <div className="space-y-0.5">
                                <TeamRow name={g.away} score={g.away_score} winner={awayWin} mine={awayMine} />
                                <TeamRow name={g.home} score={g.home_score} winner={homeWin} mine={homeMine} />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
